import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '@libs/common/components';
import { TextComponent } from '@libs/form/components';
import { FlashMessageService } from '@libs/shared/common';
import { TextInputModel } from '@libs/form/models';
import { ShopErrorItemModel, VoucherListItem } from '@libs/shop/models';
import { CartService, ShopErrorParserService } from '@libs/shared/services';
import { firstValueFrom } from 'rxjs';
import { OrderVoucherItemComponent } from './order-voucher-item';
import { VoucherInputData } from './order-voucher.model';

@Component({
  selector: 'ui-order-vouchers',
  templateUrl: './order-vouchers.component.html',
  styleUrl: './order-vouchers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TextComponent,
    ButtonComponent,
    OrderVoucherItemComponent,
  ],
})
export class OrderVouchersComponent {
  public data: InputSignal<VoucherListItem[]> = input.required();
  public voucherInputConfig: Signal<TextInputModel> = computed(() => (VoucherInputData));
  public voucherResponseControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });
  public loading: WritableSignal<boolean> = signal(false);
  public deleting: WritableSignal<boolean> = signal(false);
  private readonly cartService: CartService = inject(CartService);
  private readonly flashMessage: FlashMessageService = inject(FlashMessageService);
  private readonly shopErrorParser: ShopErrorParserService = inject(ShopErrorParserService);

  public applyVoucher(): void {
    const voucher: string = String(this.voucherResponseControl.value);

    if (!voucher.trim().length) {
      return;
    }

    this.loading.set(true);
    firstValueFrom(this.cartService.addVoucherToCart(voucher))
      .then(() => void this.flashMessage.showMessage('successAddVoucher'))
      .then(() => void this.voucherResponseControl.setValue(''))
      .catch((e) => void this.handleErrors(e))
      .finally(() => void this.loading.set(false));
  }

  public deleteVoucher(voucherCode: string): void {
    this.deleting.set(true);
    firstValueFrom(this.cartService.removeVoucherFromCart(voucherCode))
      .then(() => void this.flashMessage.showMessage('successRemoveVoucher'))
      .catch(() => void this.flashMessage.showMessage('errorRemoveVoucher'))
      .finally(() => void this.deleting.set(false));
  }

  private handleErrors(rawError: unknown): void {
    this.shopErrorParser.parseAndShowRawErrorMessages(rawError, 'errorGenericAddVoucher');
    const parsedErrors: ShopErrorItemModel[] = this.shopErrorParser.tryParseShopLlErrors(rawError);

    if (!parsedErrors.length) {
      return;
    }

    this.voucherResponseControl.setErrors({ custom: parsedErrors[0].message });
  }
}
